import { inject } from '@angular/core';
import { OpenAPIV3 } from 'openapi-types';
import { AppModel, DEV_CONFIG } from '../../shared/models/config.model';
import {
  ApiCollectionExportFormat,
  ApiCollectionFile,
  ApiCollectionVariables,
  defaultApiCollectionVariables,
  OpenApiCollectionParameter,
  OpenApiCollectionRequest,
  OpenApiCollectionRequestBody,
} from '../models/api-collection.model';
import {
  BrunoAuth,
  BrunoCollection,
  BrunoItem,
  BrunoKeyValue,
  BrunoParameter,
  BrunoRequestBody,
  BrunoVariable,
} from '../models/bruno-collection.model';
import {
  buildExportFileName,
  createStableUid,
  getBaseUrl,
  getCollectionName,
  getOpenApiCollectionRequests,
  getResource,
} from '../utils/open-api-collection-export.utils';
import { ApiCollectionService } from './api-collection-export.service';

export class BrunoApiCollectionService implements ApiCollectionService {
  readonly format: ApiCollectionExportFormat = ApiCollectionExportFormat.Bruno;

  private readonly config = inject(DEV_CONFIG);

  export(app: AppModel, document: OpenAPIV3.Document): ApiCollectionFile {
    const name = getCollectionName(app, document);
    const variables = defaultApiCollectionVariables;
    const requests = getOpenApiCollectionRequests(app, document);
    const collection = this.buildCollection(
      name,
      getBaseUrl(app, document),
      getResource(app, document),
      requests,
      variables,
    );

    return {
      fileName: buildExportFileName(name, '.bruno_collection.json'),
      contentType: 'application/json',
      content: JSON.stringify(collection, null, 2),
    };
  }

  private buildCollection(
    name: string,
    baseUrl: string,
    resource: string,
    requests: OpenApiCollectionRequest[],
    variables: ApiCollectionVariables,
  ): BrunoCollection {
    return {
      version: '1',
      uid: createStableUid(name, 0),
      name,
      items: requests.map((request, index) => this.buildRequestItem(request, index + 1, variables)),
      environments: [],
      activeEnvironmentUid: null,
      root: {
        request: {
          headers: [],
          auth: this.buildOAuth2Auth(variables),
          script: { req: '', res: '' },
          vars: {
            req: this.buildCollectionVariables(baseUrl, resource),
            res: [],
          },
          tests: '',
        },
        docs: 'Replace the OAuth variables before requesting a token.',
      },
    };
  }

  private buildOAuth2Auth(variables: ApiCollectionVariables): BrunoAuth {
    return {
      mode: 'oauth2',
      oauth2: {
        grantType: 'client_credentials',
        accessTokenUrl: variables.tokenEndpoint,
        clientId: variables.clientId,
        clientSecret: variables.clientSecret,
        scope: '.default',
        credentialsPlacement: 'basic_auth_header',
        credentialsId: 'planzer-oauth',
        tokenSource: 'access_token',
        tokenPlacement: 'header',
        tokenHeaderPrefix: 'Bearer',
        autoFetchToken: true,
        autoRefreshToken: false,
        additionalParameters: {
          token: [
            {
              name: 'resource',
              value: variables.resource,
              sendIn: 'body',
              enabled: true,
            },
          ],
          refresh: [],
        },
      },
    };
  }

  private buildCollectionVariables(baseUrl: string, resource: string): BrunoVariable[] {
    return [
      { name: 'baseUrl', value: baseUrl },
      { name: 'issuer', value: this.config.authConfig.issuer },
      { name: 'tokenEndpoint', value: this.config.authConfig.tokenEndpoint },
      { name: 'resource', value: resource },
      { name: 'clientId', value: 'Enter your clientId here' },
      { name: 'clientSecret', value: 'Enter your clientSecret here' },
    ].map((variable, index) => ({
      uid: createStableUid(variable.name, index + 2000),
      name: variable.name,
      value: variable.value,
      enabled: true,
      local: false,
    }));
  }

  private buildRequestItem(
    request: OpenApiCollectionRequest,
    sequence: number,
    variables: ApiCollectionVariables,
  ): BrunoItem {
    return {
      uid: createStableUid(request.id, sequence),
      type: 'http-request',
      name: request.name,
      seq: sequence,
      tags: request.tags,
      request: {
        url: `${variables.baseUrl}${request.path}`,
        method: request.method,
        headers: this.buildHeaders(request.body),
        params: [
          ...this.buildParams(request.pathParameters, 'path'),
          ...this.buildParams(request.queryParameters, 'query'),
        ],
        auth: { mode: 'inherit' },
        body: this.buildBody(request.body),
        script: { req: '', res: '' },
        vars: { req: [], res: [] },
        assertions: [],
        tests: '',
        docs: request.description ?? '',
      },
      examples: [],
    };
  }

  private buildHeaders(body: OpenApiCollectionRequestBody): BrunoKeyValue[] {
    if (body.mode === 'none') {
      return [];
    }

    return [
      {
        uid: createStableUid(`content-type-${body.contentType}`, 3000),
        name: 'Content-Type',
        value: body.contentType,
        enabled: true,
      },
    ];
  }

  private buildParams(parameters: OpenApiCollectionParameter[], type: 'path' | 'query'): BrunoParameter[] {
    return parameters.map((parameter, index) => ({
      uid: createStableUid(`${type}-${parameter.name}`, index + 4000),
      name: parameter.name,
      value: parameter.value,
      description: parameter.description ?? null,
      type,
      enabled: true,
    }));
  }

  private buildBody(body: OpenApiCollectionRequestBody): BrunoRequestBody {
    if (body.mode === 'none') {
      return { mode: 'none' };
    }

    if (body.mode === 'formUrlEncoded') {
      return {
        mode: 'formUrlEncoded',
        formUrlEncoded: body.parameters.map((parameter, index) => ({
          uid: createStableUid(`body-${parameter.name}`, index + 5000),
          name: parameter.name,
          value: parameter.value,
          enabled: true,
        })),
      };
    }

    if (body.contentType.includes('json')) {
      return {
        mode: 'json',
        json: body.value,
      };
    }

    if (body.contentType.includes('xml')) {
      return {
        mode: 'xml',
        xml: body.value,
      };
    }

    return {
      mode: 'text',
      text: body.value,
    };
  }
}
